import React, { Dispatch, FC, SetStateAction, memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Accordion, SectionHeader, Tag, EmptyState } from '@ets/design-system';
import cn from 'classnames';

import in_ from '_common/utils/in';

import NotApplicable from '../NotApplicable';
import { DATA_TYPE_TRANSLATIONS } from '../const';

import Table from './Table';

import styles from '../Metadata.module.scss';

type ManualMetadataProps = {
  metadata: ApiSchemas['MetadataObjectSchema'][];
  selected: number;
  select: Dispatch<SetStateAction<number>>;
  deleteMetadata: (id: string, name: string) => void;
};

const ManualMetadata: FC<ManualMetadataProps> = ({
  metadata,
  selected,
  select,
  deleteMetadata,
}) => {
  const intl = useIntl();
  const selectedMetadata = metadata[selected];

  const handleValue = () => {
    if (selectedMetadata.data_type !== 'select' && selectedMetadata.data_type !== 'multi-select') {
      return <NotApplicable place="details" />;
    }

    const tags: React.ReactElement[] = [];
    const accordions: React.ReactElement[] = [];

    selectedMetadata.options.forEach((value, optionIndex) => {
      if (value.extra.length > 0) {
        accordions.push(
          <Accordion
            variant="standard"
            key={value.fields.join('-')}
            size="m"
            testId={`selected-option-${optionIndex}`}
            title={value.fields.join('-')}
            collapsable={value.extra.length > 0}
          >
            {value.extra.map((extra, extraIndex) => (
              <div style={{ margin: value.extra.length > 0 ? '0 0 0 1rem' : '0' }}>
                <Accordion
                  variant="standard"
                  key={extra.label + '-' + extra.value}
                  title={intl.formatMessage(
                    {
                      id: 'ADDITIONAL_INFORMATION_#',
                    },
                    { number: extraIndex + 1 },
                  )}
                  size="m"
                  testId={`selected-option-${optionIndex}-extra-${extraIndex}`}
                >
                  <div style={{ margin: '0 0 0 2rem' }}>
                    <div>
                      <div className={styles.label}>
                        <FormattedMessage id="global.name" />
                      </div>
                      <div className={styles.value}>{extra.label}</div>
                    </div>
                    <div>
                      <div className={styles.label}>
                        <FormattedMessage id="VALUE" />
                      </div>
                      <div className={styles.value}>{extra.value}</div>
                    </div>
                  </div>
                </Accordion>
              </div>
            ))}
          </Accordion>,
        );
      } else {
        tags.push(
          <Tag
            label={value.fields.join('-')}
            variant="neutral"
            key={value.fields.join('-')}
            size="s"
            testId={`selected-option-${optionIndex}`}
          />,
        );
      }
    });

    return (
      <>
        {tags && tags?.length > 0 && <div className={styles.tagsValues}>{tags}</div>}
        {accordions && <div className={styles.values}>{accordions}</div>}
      </>
    );
  };

  return (
    <div className={styles.section}>
      {metadata.length === 0 ? (
        <EmptyState
          size="m"
          testId="manual-metadata"
          title={intl.formatMessage({ id: 'NO_METADATA_IN_TENANT_TITLE' })}
          description={intl.formatMessage({ id: 'NO_METADATA_IN_TENANT_BODY' })}
        />
      ) : (
        <>
          <div className={styles.table}>
            <Table
              metadata={metadata}
              selectedMetadata={selectedMetadata}
              deleteMetadata={deleteMetadata}
              select={select}
            />
          </div>
          {selectedMetadata && (
            <div className={styles.details}>
              <SectionHeader
                style={{ margin: '0 0 1rem 0' }}
                size="m"
                testId="manual-metadata-details"
                primaryAction={{
                  type: 'button',
                  icon: 'xmark',
                  variant: 'link',
                  onClick: () => {
                    select(-1);
                  },
                }}
              >
                {selectedMetadata.name}
              </SectionHeader>
              <div className={styles.content}>
                <div className={styles.field} data-testid="manual-name-field">
                  <FormattedMessage id="global.name" />
                  <div className={styles.value}>{selectedMetadata.name}</div>
                </div>
                <div className={styles.field} data-testid="manual-data-type-field">
                  <FormattedMessage id="DATA_TYPE" />
                  <div className={styles.value}>
                    {in_(selectedMetadata.data_type, DATA_TYPE_TRANSLATIONS)
                      ? DATA_TYPE_TRANSLATIONS[selectedMetadata.data_type]
                      : selectedMetadata.data_type}
                  </div>
                </div>
                <div className={styles.field} data-testid="manual-value-field">
                  <FormattedMessage id="VALUE" />
                  <div className={styles.value}>{handleValue()}</div>
                </div>
                <div className={styles.field} data-testid="manual-char-limit-field">
                  <FormattedMessage id="CHARACTER_LIMIT" />
                  <div className={styles.value}>
                    {selectedMetadata.data_type === 'string' ? (
                      selectedMetadata.char_limit
                    ) : (
                      <NotApplicable place="details" />
                    )}
                  </div>
                </div>
                <div className={styles.field} data-testid="manual-required-field">
                  <FormattedMessage id="REQUIRED" />
                  <div className={cn(styles.value, styles.capitalize)}>
                    {selectedMetadata.required.toString()}
                  </div>
                </div>
                {!selectedMetadata.automatic && (
                  <div className={styles.field} data-testid="manual-export-to-veeva-field">
                    <FormattedMessage id="EXPORT_TO_VEEVA" />
                    <div className={cn(styles.value, styles.capitalize)}>
                      {/* {/**@ts-expect-error Not yet supported in the API side} */}
                      {selectedMetadata?.confirm_pre_export?.toString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default memo(ManualMetadata);
